import type { Vector3Tuple } from 'three';

type TemplateFunction = (strings: TemplateStringsArray, name: string) => string;

const STORAGE_TOOL_LABELING_BOX_BACKGROUND_OPACITY: TemplateFunction = (
  _,
  labelName,
) => `STORAGE_TOOL_LABELING_BOX_${labelName.toUpperCase()}_BACKGROUND_OPACITY`;
const STORAGE_TOOL_LABELING_BOX_BORDER_OPACITY: TemplateFunction = (
  _,
  labelName,
) => `STORAGE_TOOL_LABELING_BOX_${labelName.toUpperCase()}_BORDER_OPACITY`;
const STORAGE_TOOL_CAMERA: TemplateFunction = (_, path) =>
  `STORAGE_TOOL_${path.toUpperCase()}_CAMERA`;

export const KEYS = {
  STORAGE_TOOL_PCD_POINT_SIZE: 'STORAGE_TOOL_PCD_POINT_SIZE',
  STORAGE_TOOL_LABELING_BOX_BACKGROUND_OPACITY:
    STORAGE_TOOL_LABELING_BOX_BACKGROUND_OPACITY,
  STORAGE_TOOL_LABELING_BOX_BORDER_OPACITY:
    STORAGE_TOOL_LABELING_BOX_BORDER_OPACITY,
  STORAGE_TOOL_CAMERA: STORAGE_TOOL_CAMERA,
  STORAGE_TOOL_ORBIT: 'STORAGE_TOOL_ORBIT',
};

export const TOOL_NAME_ICON_MAP = {
  preAnnotation: 'Robot',
  aiAssist: 'Robot',

  select: 'Robot',

  viewMode: 'Video360',
  viewModeDefault: 'Video360',
  viewModeTop: 'Bird',
  viewModeSide: 'SurfaceLeft',
  viewModeFront: 'SurfaceFront',
  fullScreen: 'SurfaceFront',

  box: 'Shape',
  boxCar: 'Car',
  boxTruck: 'Truck',
  boxPedestrian: 'Walk',
  boxCyclist: 'Bike',
  boxVan: 'Van',
  boxBus: 'Bus',

  toolPanel: 'LayoutSidebarRight',
  toolPanelShow: 'LayoutSidebarRight',
  toolPanelHide: 'LayoutSidebarRightInactive',

  labelingPannel: 'LayoutSidebar',
  labelingPannelVisible: 'LayoutSidebar',
  labelingPannelInvisible: 'LayoutSidebarInactive',

  grid: 'Frame',
  gridShow: 'Frame',
  gridHide: 'FrameOff',

  settings: 'Settings',
} as const;

export const TOOL_LABEL_COLOR_MAP = {
  CAR: import.meta.env.VITE_APP_LABELING_COLOR_CAR,
  TRUCK: import.meta.env.VITE_APP_LABELING_COLOR_TRUCK,
  PEDESTRIAN: import.meta.env.VITE_APP_LABELING_COLOR_PEDESTRIAN,
  CYCLIST: import.meta.env.VITE_APP_LABELING_COLOR_CYCLIST,
  VAN: import.meta.env.VITE_APP_LABELING_COLOR_VAN,
  BUS: import.meta.env.VITE_APP_LABELING_COLOR_BUS,
  UNKNOWN: import.meta.env.VITE_APP_LABELING_COLOR_UNKNOWN,
} as const;

export enum TOOL_CAMERA_VIEW_MODE {
  DEFAULT = 'default',
  TOP = 'top',
  SIDE = 'side',
  FRONT = 'front',
}

export enum TOOL_LABELING_BOX_WORK_TYPE {
  WORK = 'work',
  REVIEW = 'review',
}

export const TOOL_PANEL_CONTENT_TAB_VALUE = {
  LABELING_OBJECT_COUNT: 'labeling-object-count',
  FST_CAMERA: 'fst-camera',
} as const;

export const DEFAULT_CAMERA_OPTIONS_BY_VIEW_MODE = {
  TOP: {
    position: [0, 0, 7],
    rotation: [0, 0, 0],
  },
  SIDE: {
    position: [0, -1, 0],
    rotation: [0.5 * Math.PI, 0, 0],
  },
  FRONT: {
    position: [1, 0, 0],
    rotation: [0, 0.5 * Math.PI, 0.5 * Math.PI],
  },
} as {
  [K in keyof typeof TOOL_CAMERA_VIEW_MODE]: {
    position: Vector3Tuple;
    rotation: Vector3Tuple;
  };
};

export const DEFAULT_TOOL_PCD_POINT_SIZE = 1;
export const TOOL_PCD_POINT_STEP = 0.25;
export const TOOL_PCD_POINT_MIN = 0.25;
export const TOOL_PCD_POINT_MAX = 4;

export const DEFAULT_Z_GRID_HEIGHT = 10;

export const DEFAULT_TOOL_MOVE_STEP_SIZE = 0.5;
export const TOOL_MOVE_STEP_SIZE_MIN = 0.1;
export const TOOL_MOVE_STEP_SIZE_MAX = 2;
export const TOOL_MOVE_STEP_SIZE_STEP = 0.1;

export const DEFAULT_TOOL_DIMENSION_STEP_SIZE = 0.1;
export const TOOL_DIMENSION_STEP_SIZE_MIN = 0.1;
export const TOOL_DIMENSION_STEP_SIZE_MAX = 2;
export const TOOL_DIMENSION_STEP_SIZE_STEP = 0.1;

export const DEFAULT_TOOL_ROTATE_STEP_SIZE = 1;
export const TOOL_ROTATE_STEP_SIZE_MIN = 1;
export const TOOL_ROTATE_STEP_SIZE_MAX = 10;
export const TOOL_ROTATE_STEP_SIZE_STEP = 0.5;

export const TOOL_LABELING_BOX = {
  LINE: {
    DEFAULT: 0.75,
    SELECTED: 5,
  },

  OPACITY: {
    BOX: {
      MAX: 0.5,
      MIN: 0,
      STEP: 0.05,
      DEFAULT: 0.1,
      SELECTED: 0.1,
      REVIEW: 0.4,
    },
    LINE: {
      MAX: 1,
      MIN: 0.5,
      STEP: 0.05,
      DEFAULT: 0.65,
      SELECTED: 0.65,
    },
  },
} as const;

export const LABELING_TRANSLATION_KEY_MAPPER = {
  car: 'label.car.demisability',
  van: 'label.van.perfectiveness',
  bus: 'label.bus.clause',
  truck: 'label.truck.orthopterist',
  pedestrian: 'label.pedestrian.superposition',
  cyclist: 'label.cyclist.instrumentate',
} as const;
export type LABELING_TRANSLATION_KEY_MAPPER_KEY =
  keyof typeof LABELING_TRANSLATION_KEY_MAPPER;

export const ROTATION_VIEW_MODE_MAP = {
  [TOOL_CAMERA_VIEW_MODE.DEFAULT]: {
    azimuth: (-45 / 180) * Math.PI,
    polar: (60 / 180) * Math.PI,
  },
  [TOOL_CAMERA_VIEW_MODE.TOP]: {
    azimuth: 0,
    polar: 0,
  },
  [TOOL_CAMERA_VIEW_MODE.SIDE]: {
    azimuth: 0,
    polar: (90 / 180) * Math.PI,
  },
  [TOOL_CAMERA_VIEW_MODE.FRONT]: {
    azimuth: (90 / 180) * Math.PI,
    polar: (90 / 180) * Math.PI,
  },
};

export const DUMMY_PCD = (() => {
  const strDummyPcd = `# .PCD v0.7 - Point Cloud Data file format
VERSION 0.7
FIELDS x y z intensity
SIZE 4 4 4 4
TYPE F F F F
COUNT 1 1 1 1
WIDTH 0
HEIGHT 0
VIEWPOINT 0 0 0 1 0 0 0
POINTS 0
DATA binary

`;
  const encoder = new TextEncoder();

  return encoder.encode(strDummyPcd) as Uint8Array<ArrayBuffer>;
})();

export const PIVOT_CONTROLS_SCALE = {
  min: 70,
  max: 150,

  /**
   * 각각 min,max일 때의 선두께
   */
  lineWidthMin: 2,
  lineWidthMax: 6,
};

/**
 * 컨트롤러에서 카메라-타겟까지의 거리가 이것보다 짧을때만 회전을 가능하게 한다
 */
export const ENABLE_ROTATION_DISTANCE = 100;

/**
 * 카메라-타겟 거리에 따라 사용할 그리드 간격(gap) 임계값.
 * `distance` 는 **선형(미터)** 기준으로 큰 값부터 정렬되어 있다.
 * 비교 시점에 사용 측에서 필요한 단위(선형/제곱)로 변환해 사용한다.
 */
export const GRID_INTERVALS = [
  { distance: 200, gap: 40, fontSize: 6 },
  { distance: 80, gap: 20, fontSize: 2 },
  { distance: 20, gap: 10, fontSize: 1 },
  { distance: 10, gap: 2, fontSize: 0.5 },
] as const;
